'use client'

import JsonLd from '../../components/JsonLd'
import { About } from '../../components/blocks/about'
import { aboutUsContent, aboutUsKineticRoleLabels } from '../../lib/about-us-content'
import {
  CLOUD_SHUTTLE_HQ_LABEL,
  organizationAreaServedJsonLd,
  organizationPostalAddressJsonLd,
} from '../../lib/site-organization'
import { AboutProps } from '../../types'

const siteUrl = 'https://cloudshuttle.com.au'

function kineticAboutProps(): AboutProps {
  return {
    ...aboutUsContent,
    teamTitle: 'THE TEAM',
    employees: aboutUsContent.employees.map((employee, index) => ({
      ...employee,
      role: `${aboutUsKineticRoleLabels[employee.name] ?? employee.role} · NODE_${String(index + 1).padStart(2, '0')}`,
      image: employee.image?.startsWith('/') ? `${siteUrl}${employee.image}` : employee.image,
    })),
    valuesTitle: aboutUsContent.valuesTitle.toUpperCase(),
    color: 'dark',
  }
}

export default function AboutUsPage() {
  const pageContent = kineticAboutProps()

  const aboutUsJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${siteUrl}/#organization`,
        name: 'Cloud Shuttle',
        url: siteUrl,
        logo: {
          '@type': 'ImageObject',
          url: `${siteUrl}/Cloud_Shuttle_Logo_Black_v1.png`,
          width: 200,
          height: 100,
        },
        description:
          'Cloud Shuttle is an Australian and New Zealand data engineering consultancy — platforms, pipelines, and AI evaluation practice.',
        foundingDate: '2020',
        founder: { '@type': 'Person', '@id': `${siteUrl}/#peter-hanssens` },
        employee: [
          { '@type': 'Person', '@id': `${siteUrl}/#peter-hanssens` },
          { '@type': 'Person', '@id': `${siteUrl}/#ganesh-tiwari` },
        ],
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '+61-433-825-293',
          contactType: 'customer service',
          areaServed: ['AU', 'NZ'],
        },
        address: [organizationPostalAddressJsonLd()],
        areaServed: organizationAreaServedJsonLd,
        sameAs: [
          'https://www.linkedin.com/company/cloud-shuttle',
          'https://github.com/cloud-shuttle',
        ],
      },
      {
        '@type': 'Person',
        '@id': `${siteUrl}/#peter-hanssens`,
        name: 'Peter Hanssens',
        jobTitle: 'Founder and CEO',
        worksFor: { '@type': 'Organization', '@id': `${siteUrl}/#organization` },
        description: `Peter Hanssens is the founder and CEO of Cloud Shuttle. HQ at UOW Innovation Campus, North Wollongong NSW. Regular Sydney base at Stone & Chalk Tech Central, Haymarket. He organises data engineering meetups across Australia and New Zealand and leads client delivery.`,
        image: `${siteUrl}/uploads/team/peter-hanssens.jpg`,
        email: 'peter@cloudshuttle.com.au',
        telephone: '+61-433-825-293',
        knowsAbout: [
          'Data Engineering',
          'Cloud Architecture',
          'Open Source Software',
          'Community Events',
        ],
        sameAs: ['https://www.linkedin.com/in/peterhanssens/', 'https://github.com/peterhanssens'],
      },
      {
        '@type': 'Person',
        '@id': `${siteUrl}/#ganesh-tiwari`,
        name: 'Ganesh Tiwari',
        jobTitle: 'Data Engineer',
        worksFor: { '@type': 'Organization', '@id': `${siteUrl}/#organization` },
        description:
          'Ganesh Tiwari is a data engineer at Cloud Shuttle with experience across consulting and client delivery.',
        image: `${siteUrl}/uploads/team/ganesh-tiwari.png`,
        email: 'info@cloudshuttle.com.au',
        telephone: '+61-433-825-293',
        knowsAbout: ['Data Engineering', 'ETL Pipelines', 'Consulting'],
      },
      {
        '@type': 'AboutPage',
        '@id': `${siteUrl}/about-us/#webpage`,
        url: `${siteUrl}/about-us`,
        name: 'About Cloud Shuttle - Data Engineering Experts',
        description:
          'Meet the Cloud Shuttle team, our values, and how we combine community programmes with ANZ client delivery.',
        isPartOf: { '@type': 'WebSite', '@id': `${siteUrl}/#website` },
        about: { '@type': 'Organization', '@id': `${siteUrl}/#organization` },
        mainEntity: { '@type': 'Organization', '@id': `${siteUrl}/#organization` },
      },
    ],
  }

  return (
    <>
      <JsonLd data={aboutUsJsonLd} />
      <About {...pageContent} />
    </>
  )
}
